import { useState, useEffect } from 'react';
import { X, Loader2, Search, User, AlertCircle, Users, Plus } from 'lucide-react';
import { messagesApi } from '@/api/endpoints/messages.api';
import { usersApi } from '@/api/endpoints/users.api';
import { patientsApi } from '@/api/endpoints/patients.api';
import { maskCPF } from '@/lib/utils';

interface ComposeMessageDialogProps {
  patientId?: string;
  onClose: () => void;
  onSuccess: () => void;
}

interface UserOption {
  id: string;
  name: string;
  email: string;
  role: string;
}

interface PatientOption {
  id: string;
  name: string;
  cpf: string;
}

export default function ComposeMessageDialog({
  patientId: initialPatientId,
  onClose,
  onSuccess,
}: ComposeMessageDialogProps) {
  const [formData, setFormData] = useState({
    subject: '',
    content: '',
    patientId: initialPatientId || '',
    urgent: false,
  });

  const [selectedRecipients, setSelectedRecipients] = useState<UserOption[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [userSearch, setUserSearch] = useState('');

  const [patients, setPatients] = useState<PatientOption[]>([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [showPatientSearch, setShowPatientSearch] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<PatientOption | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await usersApi.getAll({ page: 0, size: 100 });
        setUsers(
          response.content
            .filter((u) => u.active)
            .map((u) => ({
              id: u.id,
              name: u.name,
              email: u.email,
              role: u.roles[0] || '',
            }))
        );
      } catch (err) {
        console.error('Erro ao carregar usuários:', err);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    if (initialPatientId) {
      const fetchPatient = async () => {
        try {
          const patient = await patientsApi.getById(initialPatientId);
          setSelectedPatient({ id: patient.id, name: patient.name, cpf: patient.cpf });
        } catch (err) {
          console.error('Erro ao carregar paciente:', err);
        }
      };
      fetchPatient();
    }
  }, [initialPatientId]);

  useEffect(() => {
    const searchPatients = async () => {
      if (patientSearch.length < 2) {
        setPatients([]);
        return;
      }
      try {
        const response = await patientsApi.getAll({ page: 0, size: 10, search: patientSearch });
        setPatients(response.content.map((p) => ({ id: p.id, name: p.name, cpf: p.cpf })));
      } catch (err) {
        console.error('Erro ao buscar pacientes:', err);
      }
    };

    const debounce = setTimeout(searchPatients, 300);
    return () => clearTimeout(debounce);
  }, [patientSearch]);

  const handleSubmit = async () => {
    if (selectedRecipients.length === 0) {
      setError('Selecione pelo menos um destinatário');
      return;
    }
    if (!formData.subject.trim()) {
      setError('Digite o assunto da mensagem');
      return;
    }
    if (!formData.content.trim()) {
      setError('Digite o conteúdo da mensagem');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await messagesApi.create({
        subject: formData.subject.trim(),
        content: formData.content.trim(),
        recipientIds: selectedRecipients.map((r) => r.id),
        patientId: formData.patientId || undefined,
        urgent: formData.urgent,
      });
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Erro ao enviar mensagem:', err);
      setError('Erro ao enviar mensagem. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const addRecipient = (user: UserOption) => {
    if (!selectedRecipients.find((r) => r.id === user.id)) {
      setSelectedRecipients([...selectedRecipients, user]);
    }
    setUserSearch('');
    setShowUserDropdown(false);
  };

  const removeRecipient = (userId: string) => {
    setSelectedRecipients(selectedRecipients.filter((r) => r.id !== userId));
  };

  const selectPatient = (patient: PatientOption) => {
    setSelectedPatient(patient);
    setFormData({ ...formData, patientId: patient.id });
    setShowPatientSearch(false);
    setPatientSearch('');
  };

  const clearPatient = () => {
    setSelectedPatient(null);
    setFormData({ ...formData, patientId: '' });
  };

  const filteredUsers = users.filter(
    (user) =>
      !selectedRecipients.find((r) => r.id === user.id) &&
      (userSearch === '' ||
        user.name.toLowerCase().includes(userSearch.toLowerCase()) ||
        user.email.toLowerCase().includes(userSearch.toLowerCase()))
  );

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      ADMIN: 'Administrador',
      DOCTOR: 'Médico',
      NURSE: 'Enfermeiro',
      RECEPTIONIST: 'Recepcionista',
    };
    return labels[role] || role;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-background p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">Nova Mensagem</h2>
          <button onClick={onClose} className="rounded p-1 hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-6 space-y-4">
          {/* Recipients */}
          <div>
            <label className="mb-1 block text-sm font-medium">
              Destinatários <span className="text-red-500">*</span>
            </label>
            {/* Selected Recipients */}
            {selectedRecipients.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-2">
                {selectedRecipients.map((recipient) => (
                  <span
                    key={recipient.id}
                    className="flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-sm text-primary"
                  >
                    <User className="h-3 w-3" />
                    {recipient.name}
                    <button
                      type="button"
                      onClick={() => removeRecipient(recipient.id)}
                      className="ml-1 rounded-full p-0.5 hover:bg-primary/20"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            {/* User Search */}
            <div className="relative">
              <div className="relative">
                <Users className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={userSearch}
                  onChange={(e) => {
                    setUserSearch(e.target.value);
                    setShowUserDropdown(true);
                  }}
                  onFocus={() => setShowUserDropdown(true)}
                  placeholder="Buscar destinatários..."
                  className="w-full rounded-lg border bg-background py-3 pl-10 pr-4"
                />
              </div>
              {showUserDropdown && filteredUsers.length > 0 && (
                <div className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border bg-background shadow-lg">
                  {filteredUsers.map((user) => (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => addRecipient(user)}
                      className="flex w-full items-center gap-2 p-3 text-left hover:bg-muted"
                    >
                      <User className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{user.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {user.email} • {getRoleLabel(user.role)}
                        </p>
                      </div>
                      <Plus className="ml-auto h-4 w-4 text-muted-foreground" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Subject */}
          <div>
            <label className="mb-1 block text-sm font-medium">
              Assunto <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              placeholder="Assunto da mensagem"
              className="w-full rounded-lg border bg-background p-3"
              maxLength={255}
            />
          </div>

          {/* Content */}
          <div>
            <label className="mb-1 block text-sm font-medium">
              Conteúdo <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder="Digite sua mensagem..."
              rows={6}
              className="w-full rounded-lg border bg-background p-3"
            />
          </div>

          {/* Patient */}
          <div>
            <label className="mb-1 block text-sm font-medium">Paciente relacionado (opcional)</label>
            {selectedPatient ? (
              <div className="flex items-center justify-between rounded-lg border bg-muted/50 p-3">
                <div className="flex items-center gap-2">
                  <User className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">{selectedPatient.name}</p>
                    <p className="text-sm text-muted-foreground">CPF: {maskCPF(selectedPatient.cpf)}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={clearPatient}
                  className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    value={patientSearch}
                    onChange={(e) => {
                      setPatientSearch(e.target.value);
                      setShowPatientSearch(true);
                    }}
                    onFocus={() => setShowPatientSearch(true)}
                    placeholder="Buscar paciente por nome ou CPF..."
                    className="w-full rounded-lg border bg-background py-3 pl-10 pr-4"
                  />
                </div>
                {showPatientSearch && patients.length > 0 && (
                  <div className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border bg-background shadow-lg">
                    {patients.map((patient) => (
                      <button
                        key={patient.id}
                        type="button"
                        onClick={() => selectPatient(patient)}
                        className="flex w-full items-center gap-2 p-3 text-left hover:bg-muted"
                      >
                        <User className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{patient.name}</p>
                          <p className="text-sm text-muted-foreground">CPF: {maskCPF(patient.cpf)}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Urgent */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="urgent"
              checked={formData.urgent}
              onChange={(e) => setFormData({ ...formData, urgent: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300"
            />
            <label htmlFor="urgent" className="flex items-center gap-2 text-sm">
              <AlertCircle className="h-4 w-4 text-red-500" />
              Marcar como urgente
            </label>
          </div>

          {/* Error */}
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg border px-4 py-2 hover:bg-muted disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Enviar Mensagem
          </button>
        </div>
      </div>
    </div>
  );
}
